<template>
  <div class="nd-page">
    <header class="nd-page-header">
      <div>
        <h1 id="title" class="page-title">Procedimentos</h1>
        <p class="nd-page-kicker">Catálogo de procedimentos clínicos utilizados nos atendimentos.</p>
      </div>
      <v-btn id="btnNew" type="button" color="primary" elevation="0" to="/procedimentos/novo">
        <v-icon left small>mdi-plus</v-icon>
        Incluir
      </v-btn>
    </header>

    <module-stat-grid module="procedures" :items="dataList.data" :count="dataList.count" />

    <v-card class="nd-section-card">
      <div class="nd-table-toolbar">
        <div>
          <div class="nd-table-toolbar__title">Procedimentos</div>
          <div class="nd-table-toolbar__subtitle">Filtre, revise e atualize os procedimentos disponíveis.</div>
        </div>
        <v-form class="nd-table-toolbar__actions" @submit.prevent="load()">
          <v-text-field
            id="name"
            class="nd-search-field"
            dense
            placeholder="Buscar procedimento"
            v-model="dataList.filter.name" hide-details="auto"
            outlined
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
          <v-btn id="btnFilter" type="submit" outlined color="primary" elevation="0">
            <v-icon left size="18">mdi-filter-outline</v-icon>
            Filtrar
          </v-btn>
        </v-form>
      </div>
            <v-data-table
              :headers="dataList.headers"
              :items="dataList.data"
              @update:items-per-page="pageSizeChanged"
              @update:page="pageChanged"
              :server-items-length="dataList.count"
              :disable-sort="true"
              :loading="dataList.loading"
              no-data-text="Nenhum procedimento encontrado"
              loading-text="Carregando dados..."
              class="app-table hover-table"
              :options.sync="dataList.options"
              :footer-props="dataList.footerOptions"
            >
              <template v-slot:[`item.name`]="{ item }">
                <div class="nd-entity-cell">
                  <span class="nd-entity-mark">{{ item.name ? item.name.charAt(0) : "P" }}</span>
                  <div>
                    <strong>{{ item.name }}</strong>
                    <small>ID {{ item.id }}</small>
                  </div>
                </div>
              </template>

              <template v-slot:[`item.date`]="{ item }">
                {{ formatDate(item.date) }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="row-actions">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="viewItem(item)">
                        <v-icon size="19">
                        mdi-pencil-outline
                      </v-icon>
                      </v-btn>
                    </template>
                    <span>Atualizar procedimento</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small v-on="on" @click="deleteProcedure(item)">
                        <v-icon size="19">
                        mdi-trash-can-outline
                      </v-icon>
                      </v-btn>
                    </template>
                    <span>Remover procedimento</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
    </v-card>
  </div>
</template>

<script>
import ModuleStatGrid from "@/components/ModuleStatGrid.vue";
import Vue from "vue";
import axios from "axios";

export default {
  components: { ModuleStatGrid },
  name: "ProceduresList",

  data: () => ({
    dataList: {
      filter: {
        name: "",
      },
      headers: [
        { text: "Nome", value: "name" },
        { text: "Data", value: "date", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      data: [],
      loading: false,
      count: 0,
      options: {
        page: 1,
        size: 10,
        sortBy: ["id"],
        sortDesc: [false],
      },
      footerOptions: {
        itemsPerPageText: "Itens por página:",
        itemsPerPageOptions: [10, 20, 30, 40, 50],
        pageText: "{0}-{1} de {2}",
      },
    },
  }),

  created() {
    this.load();
  },

  computed: {
    lastRecordDate() {
      const latest = this.dataList.data.find((item) => item.date);
      return latest ? this.formatDate(latest.date) : "-";
    },
  },

  methods: {
    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    pageChanged(page) {
      this.dataList.options.page = page;
      this.load();
    },

    pageSizeChanged(size) {
      this.dataList.options.size = size;
      this.load(true);
    },

    async load() {
      const params = {
        start: (this.dataList.options.page - 1) * (this.dataList.options.itemsPerPage || 10),
        limit: this.dataList.options.itemsPerPage || 10,
      };

      if (this.dataList.filter.name) {
        params.search = this.dataList.filter.name;
      }

      this.dataList.loading = true;

      try {
        const result = await axios.get("/procedures", { params });

        this.dataList.data = result.data.data;
        this.dataList.count = result.data.count;
      } catch (error) {
        this.$dialog.notify.error(
          "Ocorreu um erro ao carregar os procedimentos. Tente novamente.",
          { position: "top-right", timeout: 5000 }
        );
      } finally {
        this.dataList.loading = false;
      }
    },

    viewItem(item) {
      this.$router.push(`/procedimentos/${item.id}`);
    },

    async deleteProcedure(item) {
      const proceed = await this.$dialog.confirm({
        text: `Deseja realmente remover o procedimento "${item.name}"?`,
        title: "Confirmar Exclusão",
        actions: {
          false: "Cancelar",
          true: { text: "Remover", color: "error" },
        },
      });

      if (proceed) {
        this.dataList.loading = true;
        try {
          await axios.delete(`/procedures/${item.id}`);
          this.$dialog.notify.info(`Procedimento "${item.name}" removido com sucesso!`, {
            position: "top-right",
            timeout: 2000,
          });
          this.load();
        } catch (error) {
          this.$dialog.notify.error(
            "Ocorreu um erro ao remover o procedimento. Tente novamente.",
            { position: "top-right", timeout: 5000 }
          );
        } finally {
          this.dataList.loading = false;
        }
      }
    },
  },
};
</script>
